import { Fragment, useState } from 'react';
import { useRematchMtoOffersMutation } from '@shared/api';
import type { UnmatchedOffer } from '@entities/mto';
import { apiErrorMessage, fmtDate } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import {
  Caption,
  CaptionAction,
  DetailCell,
  ExpandButton,
  Muted,
  Notice,
  OfferMeta,
  PriceCell,
  Table,
  TableScroll,
} from '../styles';
import { NomenclaturePicker } from './NomenclaturePicker';

export function UnmatchedSection({
  items,
  runId,
  canWrite,
  onError,
}: {
  items: UnmatchedOffer[];
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [rematch, { isLoading: rematching, data: report }] = useRematchMtoOffersMutation();

  async function handleRematch() {
    onError('');
    try {
      await rematch({ runId, scope: 'all' }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось подобрать КП по справочнику.');
      // eslint-disable-next-line no-console
      console.error('rematchMtoOffers failed', err);
    }
  }

  return (
    <>
      <Caption>
        Не сопоставлено со справочником <Badge $tone="warning">{items.length}</Badge>
        {canWrite && (
          <CaptionAction>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={handleRematch}
              disabled={rematching}
              title="Подобрать все КП базы к справочнику заново — например, после его дополнения"
            >
              {rematching ? 'Подбираем…' : 'Подобрать по справочнику'}
            </Button>
          </CaptionAction>
        )}
      </Caption>
      <Muted>
        Помощник не нашёл этим позициям однозначного соответствия в справочнике, поэтому
        сравнивать их не с чем. Нажмите на название и выберите позицию справочника или добавьте
        новую — привязка запомнится, и следующие КП с таким же наименованием сопоставятся сами.
      </Muted>
      {report && (
        <Notice role="status">
          Просмотрено строк КП: {report.checked}, привязано к справочнику: {report.matched}
          {report.repriced > 0 ? `, цен распознано: ${report.repriced}` : ''}.
        </Notice>
      )}
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <th scope="col">Позиция в КП</th>
              <th scope="col">Поставщик</th>
              <th scope="col" className="num">
                Цена
              </th>
              <th scope="col">Ед.</th>
              <th scope="col">Источник</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <Fragment key={o.offer_id}>
                <tr>
                  <td>
                    {canWrite ? (
                      <ExpandButton
                        type="button"
                        $open={openId === o.offer_id}
                        onClick={() => setOpenId((cur) => (cur === o.offer_id ? null : o.offer_id))}
                        aria-expanded={openId === o.offer_id}
                      >
                        {openId === o.offer_id ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                        {o.position_name}
                      </ExpandButton>
                    ) : (
                      o.position_name
                    )}
                  </td>
                  <td>{o.supplier_name ?? '—'}</td>
                  <PriceCell>{o.price ?? '—'}</PriceCell>
                  <td>{o.unit ?? '—'}</td>
                  <td>
                    <OfferMeta title={o.source_ref}>{fmtDate(o.created_at)}</OfferMeta>
                  </td>
                </tr>
                {openId === o.offer_id && (
                  <tr>
                    <DetailCell colSpan={5}>
                      <NomenclaturePicker offer={o} runId={runId} onError={onError} />
                    </DetailCell>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </>
  );
}
